/**
 * 1b/V85 — Configuración → "Datos fiscales" (solo SUPER_USER, kill-switch ON):
 * toggle de opt-in "Quiero factura (CFDI) de mi suscripción" + datos del tenant
 * como RECEPTOR (GET/PUT /me/fiscal-data). Cambiar a Sí exige capturar datos
 * (una factura sin datos no existe); cambiar a No guarda el opt-out y esconde
 * los campos (sus cobros quedan `opted_out`, sin avisos). El cobro NUNCA se
 * bloquea por esto (decisión 1b: avisar, no bloquear).
 * AUTO-GATEADA por useAuth (patrón PaymentMethodSection, lección te18).
 * Campos/validación compartidos con el SubscribeDialog (FiscalDataFields).
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  EMPTY_FISCAL, FiscalData, FiscalDataFields, FiscalDataResponse,
  fiscalErrMsg, getFiscalData, saveCfdiOptin, saveFiscalData,
} from './FiscalDataFields';

export function FiscalDataCard() {
  const { user } = useAuth() as any;
  const checkoutEnabled = !!user?.billing?.checkoutEnabled;
  const isSuper = user?.role === 'SUPER_USER';
  // V217 (§1ter): los datos fiscales del receptor son del TITULAR (el backend da 403 a otros).
  const isOwner = !!user?.isOwner;

  const [form, setForm] = useState<FiscalData>(EMPTY_FISCAL);
  const [catalogs, setCatalogs] = useState<FiscalDataResponse['catalogs'] | null>(null);
  const [complete, setComplete] = useState(false);
  // Toggle: FALSE explícito = apagado; TRUE o NULL (no decidido) = encendido
  // (el indeciso ve los campos y el banner — mismo criterio que el backend).
  const [wants, setWants] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    if (!checkoutEnabled || !isSuper || !isOwner) return;
    let cancelled = false;
    getFiscalData()
      .then((r) => {
        if (cancelled) return;
        setCatalogs(r.catalogs);
        setComplete(r.complete);
        setWants(r.wantsCfdi !== false);
        if (r.data) setForm({
          rfc: r.data.rfc, razonSocial: r.data.razonSocial, regimenFiscal: r.data.regimenFiscal,
          usoCfdi: r.data.usoCfdi, cp: r.data.cp,
        });
      })
      .catch(() => { /* la card sigue usable con defaults */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [checkoutEnabled, isSuper, isOwner]);

  if (!checkoutEnabled || !isSuper) return null;
  if (!isOwner) {
    return (
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Datos fiscales</h3>
        <p className="text-sm text-slate-500">
          Los datos fiscales para la factura de tu suscripción los captura el titular de la cuenta
          {user?.ownerName ? ` (${user.ownerName})` : ''}.
        </p>
      </div>
    );
  }

  const flash = (msg: string) => { setOk(msg); setTimeout(() => setOk(null), 4000); };

  const save = async () => {
    setBusy(true); setErr(null); setOk(null);
    try {
      if (!wants) {
        await saveCfdiOptin(false);
        flash('Preferencia guardada: no emitiremos factura de tus pagos.');
      } else {
        // Guardar datos implica opt-in TRUE (lo setea el backend).
        await saveFiscalData(form);
        setComplete(true);
        flash('Datos guardados ✓ — si tenías facturas pendientes por falta de datos, se emitirán en unos minutos.');
      }
    } catch (e: any) {
      setErr(fiscalErrMsg(e, 'No se pudo guardar.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Datos fiscales</h3>
        <p className="text-sm text-slate-500">
          Con estos datos emitimos la <strong>factura (CFDI)</strong> de tus pagos de suscripción y te la
          enviamos por correo (XML + PDF).
        </p>
      </div>
      <div className="space-y-4 px-6 py-5 md:px-8">
        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={wants}
            onChange={(e) => { setWants(e.target.checked); setErr(null); }}
            className="h-4 w-4 rounded border-slate-300 accent-slate-900"
          />
          <span className="text-sm font-medium text-slate-800">
            Quiero factura (CFDI) de mi suscripción
          </span>
        </label>

        {!wants && (
          <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            No emitiremos factura de tus pagos. Puedes activarlo cuando quieras; aplicará
            a tus cobros siguientes.
          </p>
        )}

        {wants && (
          <>
            {!loading && !complete && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Aún no tienes datos fiscales capturados: tus pagos se procesan normal, pero
                <strong> no podremos emitirte factura</strong> hasta completarlos. Captúralos
                <strong> exactamente como aparecen en tu Constancia de Situación Fiscal</strong>.
              </div>
            )}
            <FiscalDataFields form={form} onChange={setForm} catalogs={catalogs} />
          </>
        )}

        {err && <p className="text-sm text-rose-600">{err}</p>}
        {ok && <p className="text-sm text-emerald-600">{ok}</p>}
        <div className="flex justify-end">
          <button onClick={save} disabled={busy || loading}
            className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {busy ? 'Guardando…' : wants ? 'Guardar datos fiscales' : 'Guardar preferencia'}
          </button>
        </div>
      </div>
    </div>
  );
}
